// -----------------------------------------------------------------------------
// useLeadReminderTasks
//
// Thin wrapper around useV2ChecklistAutocomplete that adds the derived values
// and dispatch-routing wrappers that FollowUpV2 (and the shared
// ScheduleReminderTaskSection) need.
//
// Returns:
//   items, openItemsByType, rawGroups, loading, refresh  — from useV2ChecklistAutocomplete
//   activeTaskIds        — deduplicated OPEN task IDs (memoized)
//   activeTaskCount      — activeTaskIds.length
//   completeItem(item, { onSuccess })
//     — routes to completeTask (standalone) vs completeChecklistItem (checklist).
//       Caller passes onSuccess callback to run animation/featured-advance side-effects.
//       Re-entrancy guard lives on completingItemIdsRef (shared with handlePanelComplete).
//   reopenItem(item, { onSuccess })
//     — wraps reopenChecklistItem; calls onSuccess({ success }) on response.
//   pauseItem(item, pausedUntilISO)
//     — returns the callApi promise for /pause/ (caller handles optimistic state).
//   resumeFirstPaused({ onSuccess, onSettled })
//     — reopens the first PAUSED item; clears optimistic state before POST.
//   completingItemIdsRef
//     — the shared re-entrancy guard Set ref (used by both completeItem +
//       handlePanelComplete inside ScheduleReminderTaskSection).
// -----------------------------------------------------------------------------
/* eslint-disable react-hooks/exhaustive-deps */
import { useMemo, useRef, useCallback } from 'react';
import { callApi } from 'src/utils/apiCall';
import { dispatch } from 'src/redux/store';
import { completeChecklistItem, completeTask, reopenChecklistItem, reopenTask } from 'src/redux/slices/taskSlice';
import { CHECKLIST_COMPLETION_STATE } from 'src/constants/tasksV2';
import { resolveTaskUndoTarget, TASK_UNDO_TARGET_TYPE } from 'src/utils/taskUndoTarget';
import useV2ChecklistAutocomplete from './useV2ChecklistAutocomplete';

export default function useLeadReminderTasks({ practiceId, leadId }) {
    const checklist = useV2ChecklistAutocomplete({ practiceId, leadId });
    const { items, refresh } = checklist;

    // Re-entrancy guard — prevents double-POSTing the same complete/ if button clicked twice.
    // Mirrors AGCv2.jsx:216 completingItemIdsRef. Shared between completeItem +
    // handlePanelComplete inside ScheduleReminderTaskSection.
    const completingItemIdsRef = useRef(new Set());

    // Active task IDs — deduplicated task IDs of OPEN items.
    // Lifted verbatim from FollowUpV2 (no logic change).
    const activeTaskIds = useMemo(
        () => [...new Set(items.filter((i) => i.completion_state === 'OPEN').map((i) => i.taskId).filter(Boolean))],
        [items]
    );
    const activeTaskCount = activeTaskIds.length;

    // completeItem — routes completeTask (standalone) vs completeChecklistItem (checklist).
    // Lifted from handleCardMarkComplete in FollowUpV2. Caller passes onSuccess callback
    // for animation/featured-advance side-effects (those stay in ScheduleReminderTaskSection).
    const completeItem = useCallback((item, { onSuccess } = {}) => {
        if (!item?.taskId) return;

        // Standalone tasks (template=null, no checklist-item row) — use task-level complete.
        if (item.isStandaloneTask) {
            dispatch(completeTask(practiceId, item.taskId, (result) => {
                if (result.success) {
                    refresh({ bustCache: true });
                }
                if (onSuccess) onSuccess(result);
            }));
            return;
        }

        if (!item?.itemId) return;
        const inFlightKey = `${item.taskId}:${item.itemId}`;
        if (completingItemIdsRef.current.has(inFlightKey)) return;
        completingItemIdsRef.current.add(inFlightKey);

        const payload = item.resolvedAction ? { resolved_action: item.resolvedAction } : null;
        dispatch(completeChecklistItem(
            practiceId, item.taskId, item.itemId,
            (result) => {
                completingItemIdsRef.current.delete(inFlightKey);
                if (result.success) {
                    refresh({ bustCache: true });
                }
                if (onSuccess) onSuccess(result);
            },
            payload,
            CHECKLIST_COMPLETION_STATE.YES,
        ));
    }, [practiceId, refresh]);

    // reopenItem — routes standalone task undo vs checklist-item undo.
    const reopenItem = useCallback((item, { onSuccess } = {}) => {
        const undoTarget = resolveTaskUndoTarget(item);
        if (!undoTarget) return;
        if (undoTarget.type === TASK_UNDO_TARGET_TYPE.TASK) {
            dispatch(reopenTask(
                practiceId, undoTarget.taskId,
                (result) => {
                    if (result.success) refresh({ bustCache: true });
                    if (onSuccess) onSuccess(result);
                }
            ));
            return;
        }
        dispatch(reopenChecklistItem(
            practiceId, undoTarget.taskId, undoTarget.itemId,
            (result) => {
                if (result.success) refresh({ bustCache: true });
                if (onSuccess) onSuccess(result);
            }
        ));
    }, [practiceId, refresh]);

    // pauseItem — POSTs /pause/ and returns the callApi promise.
    // Caller handles optimistic state (ScheduleReminderTaskSection).
    const pauseItem = useCallback((item, pausedUntilISO) =>
        callApi(
            `/api/v1/practices/${practiceId}/tasks/${item.taskId}/checklist/${item.itemId}/pause/`,
            'post',
            { paused_until: pausedUntilISO }
        ),
    [practiceId]);

    // resumeFirstPaused — reopens the first PAUSED item; server cascade-resumes all.
    // Mirrors AGCv2.jsx:1112-1129 (handleResumeChecklist).
    // onSuccess called when the reopen POST returns success.
    // onSettled called regardless of success/failure (for clearing loading state).
    const resumeFirstPaused = useCallback(({ onSuccess, onSettled } = {}) => {
        const firstPaused = items.find((i) => i.completion_state === CHECKLIST_COMPLETION_STATE.PAUSED);
        if (!firstPaused?.taskId || !firstPaused?.itemId) {
            if (onSettled) onSettled();
            return;
        }
        dispatch(reopenChecklistItem(
            practiceId, firstPaused.taskId, firstPaused.itemId,
            ({ success }) => {
                if (onSettled) onSettled();
                if (success) {
                    if (onSuccess) onSuccess();
                    refresh({ bustCache: true });
                }
            }
        ));
    }, [items, practiceId, refresh]);

    return {
        ...checklist,
        activeTaskIds,
        activeTaskCount,
        completingItemIdsRef,
        completeItem,
        reopenItem,
        pauseItem,
        resumeFirstPaused,
    };
}
